/**
 * SOLID Review
 * S — Strong. One concern: cookie consent as data — the CMP's state, the session's copy of it, and the
 *     audit record the server keeps.
 * O — Good. A new consent category is one boolean in `ClientCookieCategories`.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the consent moves.
 * D — Good. zod and the legal moves' document shape.
 */
import { z } from 'zod';

/**
 * COOKIE CONSENT (frontend strict-ports plan, FE4b, 2026-10-04). The consent management platform (Google
 * Funding Choices, or the first-party preferences modal) decides what this browser may store; the client
 * keeps the latest state in the session and the server keeps an audit record of each resolved decision.
 * CookieConsentProvider did both from React, with a module-level "last recorded" signature; the decision
 * and the signature are the Domain's and the session's now.
 */

/** Google Consent Mode's five categories, all required. */
const ClientCookieCategories = z.object({
  adStorage: z.boolean(),
  adUserData: z.boolean(),
  adPersonalization: z.boolean(),
  analyticsStorage: z.boolean(),
  functionality: z.boolean(),
}).meta({ id: 'ClientCookieCategories' });

const ConsentSource = z.enum(['TCF', 'US', 'MANUAL']);

/** The CMP's state as it reports it: resolved or still open, the categories, where, how. */
export const PayloadConsentState = z.object({
  resolved: z.boolean(),
  categories: ClientCookieCategories,
  region: z.string(),
  source: ConsentSource,
  tcString: z.string().nullable(),
}).meta({ id: 'PayloadConsentState' });

/**
 * The session's consent: the CMP's state as React reads it — `advertisingAllowed` decided once, in the
 * Domain — and the signature of the last decision the server was told about, so one decision is recorded
 * once (CookieConsentProvider's module-level `lastRecordedSignature`).
 */
export const ClientConsent = z.object({
  resolved: z.boolean(),
  categories: ClientCookieCategories,
  region: z.string(),
  source: ConsentSource,
  advertisingAllowed: z.boolean(),
  recordedSignature: z.string().nullable(),
}).meta({ id: 'ClientConsent' });

/** The audit record, as the client sends it; the consent id is minted by Infrastructure. */
export const RequestCookieConsent = z.object({
  categories: ClientCookieCategories,
  tcString: z.string().nullable(),
  source: ConsentSource,
  region: z.string(),
  /** The Cookie Policy version in force — the one the server publishes (FE4b's fix). */
  policyVersion: z.string(),
}).meta({ id: 'RequestCookieConsent' });

export const CommandCookiePreferences = z.object({ categories: ClientCookieCategories }).meta({ id: 'CommandCookiePreferences' });
export const DtoPreferencesOpened = z.object({ opened: z.boolean() }).meta({ id: 'DtoPreferencesOpened' });

// ── the decisions' commands ───────────────────────────────────────────────────────────────────

export const CommandConsentChanged = z.object({ state: PayloadConsentState }).meta({ id: 'CommandConsentChanged' });
/** Whether this state is a resolved decision the server has not been told about. */
export const DtoConsentRecord = z.object({ record: z.boolean() }).meta({ id: 'DtoConsentRecord' });
export const CommandCookieConsentRecord = z.object({
  state: PayloadConsentState,
  /** The published documents' kinds and versions — the Cookie Policy's version stamps the record. */
  published: z.array(z.object({ type: z.string(), version: z.string() })),
}).meta({ id: 'CommandCookieConsentRecord' });
export const DtoCookieConsentRecord = z.object({ request: RequestCookieConsent }).meta({ id: 'DtoCookieConsentRecord' });

export type IPayloadConsentState = z.infer<typeof PayloadConsentState>;
export type IEntityClientConsent = z.infer<typeof ClientConsent>;
export type IPayloadCookieConsent = z.infer<typeof RequestCookieConsent>;
export type ICommandCookiePreferences = z.infer<typeof CommandCookiePreferences>;
export type IEntityPreferencesOpened = z.infer<typeof DtoPreferencesOpened>;
export type ICommandConsentChanged = z.infer<typeof CommandConsentChanged>;
export type IEntityConsentRecord = z.infer<typeof DtoConsentRecord>;
export type ICommandCookieConsentRecord = z.infer<typeof CommandCookieConsentRecord>;
export type IEntityCookieConsentRecord = z.infer<typeof DtoCookieConsentRecord>;
